// Self-serve demo: /d
//
// Someone (e.g. Natalya at Archway) enters the access code we gave them, their name and
// mobile number. Their demo
// account resets, Sophia texts them right away and calls about a minute later about a
// sample balance. The page says exactly that before they submit. Part of the dunning
// demo; delete this file and its route in App.js to remove it.
import React, { useEffect, useState } from 'react';
import { PAYMENTS_BASE_URL } from '../services/common';

const API = `${process.env.REACT_APP_DUNNING_DEMO_API_URL || PAYMENTS_BASE_URL}/api/dunning-demo/try`;

const prettyPhone = (e164) => {
  const d = String(e164 || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164 || '';
};

export default function TryDemoPage() {
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [started, setStarted] = useState(null);

  useEffect(() => {
    fetch(API).then((r) => r.json()).then(setInfo).catch(() => setInfo({}));
  }, []);
  useEffect(() => {
    document.title = `${info?.practiceName || 'Newton'} · Try Sophia`;
  }, [info]);

  const practice = info?.practiceName || 'the practice';
  const balance = (info?.balanceDollars || 6600).toLocaleString('en-US');
  const delay = info?.callDelaySeconds || 60;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const resp = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, consent, code: code.trim() }),
      });
      const body = await resp.json();
      if (!body.success) throw new Error(body.error || 'Something went wrong. Please try again.');
      setStarted(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const brand = info?.brand;
  return (
    <div style={{ minHeight: '100vh', background: brand?.background || 'var(--pm-page)', padding: '32px 16px',
      ...(brand ? { '--pm-brand': brand.primary, '--pm-brand-hover': brand.primary } : {}) }}>
      <div className="pm-card" style={{ maxWidth: 520, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          {brand?.logoSvg && (
            <img alt="" style={{ height: 36, width: 'auto' }}
              src={`data:image/svg+xml;utf8,${encodeURIComponent(brand.logoSvg)}`} />
          )}
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pm-brand)', letterSpacing: '0.04em',
            textTransform: 'uppercase' }}>
            {info?.practiceName || ''}
          </div>
        </div>

        {started ? (
          <>
            <h1 style={styles.h1}>Check your phone 📱</h1>
            <ol style={styles.steps}>
              <li><strong>Now:</strong> a text from Sophia at {prettyPhone(started.textFrom)} about your
                ${balance} balance, with a secure payment link.</li>
              <li><strong>In about {Math.round(started.callInSeconds / 60) || 1} minute:</strong> Sophia
                calls you from {prettyPhone(started.callFrom)}. Pick up and talk to her.</li>
            </ol>
            <TryTips />
            <button type="button" style={styles.link} onClick={() => setStarted(null)}>
              ← Start over with another number
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <h1 style={styles.h1}>Meet Sophia, the AI that collects patient balances</h1>
            <p style={styles.p}>
              Enter your name and mobile number to experience it as a patient of {practice} with
              a <strong>${balance}</strong> past-due balance:
            </p>
            <ol style={styles.steps}>
              <li><strong>Right away</strong>, Sophia texts you about the balance with a secure payment
                link{info?.textFrom ? ` (from ${prettyPhone(info.textFrom)})` : ''}.</li>
              <li><strong>About {Math.round(delay / 60) || 1} minute later</strong>, she calls
                you{info?.callFrom ? ` from ${prettyPhone(info.callFrom)}` : ''}.</li>
              <li>Reply to her text or talk to her on the call, the way a real patient would.</li>
            </ol>

            <label style={styles.label}>Access code
              <input style={styles.input} value={code} onChange={(e) => setCode(e.target.value)}
                autoComplete="off" autoCapitalize="none" spellCheck={false}
                placeholder="From your Newton contact" required />
            </label>
            <label style={styles.label}>Your name
              <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)}
                autoComplete="name" placeholder="First and last name" required />
            </label>
            <label style={styles.label}>Mobile number
              <input style={styles.input} value={phone} onChange={(e) => setPhone(e.target.value)}
                type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" required />
            </label>
            <label style={styles.consent}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 3 }} />
              <span>I agree to receive one demo text and one demo call from Sophia (an AI assistant) at
                this number. Message and data rates may apply. Reply STOP to opt out.</span>
            </label>

            {error && <p style={{ color: 'var(--pm-danger)', marginTop: 12 }}>{error}</p>}
            <button type="submit" className="pm-btn-primary" style={styles.cta} disabled={busy}>
              {busy ? 'Starting…' : 'Text me, then call me'}
            </button>
            <p style={{ ...styles.small, marginTop: 14 }}>
              This is a demo. The balance is made up and no real money is charged: the payment page
              only accepts test cards.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function TryTips() {
  return (
    <div style={styles.tips}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Things to try</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>"I can't pay all of that." Sophia helps you set up a plan: your own down payment and months.</li>
        <li>"Can I make a partial payment?" Any amount, no plan needed.</li>
        <li>"Why do I owe this?" She explains the visits and what insurance paid.</li>
        <li>Agree to pay on the call: she texts the link and waits while you pay.</li>
      </ul>
    </div>
  );
}

const styles = {
  h1: { fontSize: '1.45rem', margin: '0 0 10px', color: 'var(--pm-ink-strong)', lineHeight: 1.25 },
  p: { color: 'var(--pm-text)', margin: '6px 0 4px' },
  steps: { color: 'var(--pm-text)', paddingLeft: 20, margin: '10px 0 18px', lineHeight: 1.5 },
  small: { fontSize: '0.82rem', color: 'var(--pm-muted)' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--pm-text)', marginTop: 12 },
  input: { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '10px 12px',
    borderRadius: 8, border: '1px solid var(--pm-line-input)', fontSize: '1rem' },
  consent: { display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, fontSize: '0.82rem',
    color: 'var(--pm-muted)', lineHeight: 1.4 },
  cta: { width: '100%', marginTop: 18, padding: '12px 16px', fontSize: '1rem' },
  tips: { marginTop: 8, padding: '12px 14px', borderRadius: 10, background: 'var(--pm-surface-muted)',
    fontSize: '0.88rem', color: 'var(--pm-text)', lineHeight: 1.5 },
  link: { background: 'none', border: 'none', padding: 0, marginTop: 16, color: 'var(--pm-brand)',
    cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 },
};
